<template>
  <div class="cards-view">
    <div class="cards-toolbar">
      <v-btn icon @click="$emit('prev-month')"><v-icon>mdi-chevron-left</v-icon></v-btn>
      <slot name="month" />
      <v-btn icon @click="$emit('next-month')"><v-icon>mdi-chevron-right</v-icon></v-btn>
      <v-spacer />
      <v-btn icon small color="primary" @click="$emit('add-seller')">
        <v-icon small>mdi-account-plus</v-icon>
      </v-btn>
      <v-btn
        v-if="editing"
        outlined
        color="primary"
        small
        :loading="copying"
        @click="$emit('copy-month')"
      >
        <v-icon left small>mdi-content-copy</v-icon>
        Copiar do mês anterior
      </v-btn>
      <f-btn v-else edit @click="$emit('start-edit')" />
    </div>

    <div class="cards-search">
      <v-icon small color="grey">mdi-magnify</v-icon>
      <input
        :value="search"
        placeholder="Pesquise.."
        class="cards-search__input"
        @input="$emit('update:search', $event.target.value)"
      />
    </div>

    <div class="cards-list" :class="{ 'is-readonly': !editing }">
      <div v-for="row in filteredRows" :key="row.employee_id" class="seller-card">
        <div class="seller-card__head" @click="toggle(row.employee_id)">
          <div class="seller-avatar">{{ initials(row.employee_name) }}</div>
          <div class="seller-card__meta">
            <div class="seller-card__name">{{ row.employee_name }}</div>
            <div class="seller-card__resumo">{{ resumo(row) }}</div>
          </div>
          <v-btn icon x-small @click.stop="$emit('remove-seller', row)">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
          <v-icon color="blue-grey lighten-2">
            {{ expandedId === row.employee_id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </div>

        <div v-if="expandedId === row.employee_id" class="seller-card__body">
          <div class="seller-card__tabs">
            <button
              v-for="(tab, idx) in tabs"
              :key="tab"
              type="button"
              class="seller-card__tab"
              :class="{ 'seller-card__tab--active': tabIdx === idx }"
              @click="tabIdx = idx"
            >
              {{ tab }}
            </button>
          </div>

          <div class="seller-card__fields" v-if="tabIdx === 0">
            <div class="field">
              <div class="field__label">Tipo Comissão Cimento</div>
              <select v-model="row.cement_comission_type" class="field__select" :disabled="!editing" @change="markDirty(row)">
                <option value="per_bag">Por saco (R$)</option>
                <option value="per_revenue">Por faturamento (%)</option>
              </select>
            </div>
            <div class="field">
              <div class="field__label">Comissão Cimento</div>
              <div class="field__input-wrap">
                <span v-if="row.cement_comission_type !== 'per_revenue'" class="field__pre">R$</span>
                <input v-model.number="row.cement_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
                <span class="field__suf">{{ row.cement_comission_type === 'per_revenue' ? '% fat.' : '/saco' }}</span>
              </div>
            </div>
            <div class="field">
              <div class="field__label">Marca Fassa</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.fassa_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Outros Produtos</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.other_products_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Cal Galpão</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.yard_cal_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Lucratividade Bônus</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profitability_bonus" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Aumento de Margem</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profitability_margin_increase" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Margem Galpão</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profitability_yard_margin" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Margem GC</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profitability_gc_margin" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
          </div>

          <div class="seller-card__fields" v-else-if="tabIdx === 1">
            <div class="field">
              <div class="field__label">Meta Volume</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.volume_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Super Meta Volume</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.volume_super_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Hiper Meta Volume</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.volume_hyper_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Tipo Meta Cliente</div>
              <select v-model="row.client_goal_type" class="field__select" :disabled="!editing" @change="markDirty(row)">
                <option value="fixed">Fixo</option>
                <option value="variable">Variável</option>
              </select>
            </div>
            <div class="field">
              <div class="field__label">Meta Cliente</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.client_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Super Meta Cliente</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.client_super_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Hiper Meta Cliente</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.client_hyper_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Meta Lucro</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profit_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Super Meta Lucro</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profit_super_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Hiper Meta Lucro</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.profit_hyper_goal" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
          </div>

          <div class="seller-card__fields" v-else>
            <div class="field">
              <div class="field__label">Clientes Reativados</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.reactivated_clients_bonus" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
            <div class="field">
              <div class="field__label">Novos Clientes</div>
              <div class="field__input-wrap">
                <span class="field__pre">R$</span>
                <input v-model.number="row.new_clients_bonus" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty(row)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!filteredRows.length" class="pa-6 caption grey--text text-center">
        Nenhum vendedor encontrado
      </div>
    </div>

    <div v-if="editing" class="cards-footer d-flex">
      <f-btn cancel class="mr-2 flex-grow-1" :disabled="saving" @click="$emit('cancel-edit')" />
      <f-btn save class="flex-grow-1" :disabled="!dirtyCount || saving" :loading="saving" @click="$emit('save-all')" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'SellerComissionCardsView',
  props: {
    rows: { type: Array, required: true },
    search: { type: String, default: '' },
    editing: { type: Boolean, default: false },
    copying: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    resumoFn: { type: Function, required: true },
    initialsFn: { type: Function, required: true },
  },
  data() {
    return {
      expandedId: null,
      tabIdx: 0,
      tabs: ['COMISSÕES', 'METAS', 'BÔNUS'],
    }
  },
  computed: {
    filteredRows() {
      const q = (this.search || '').toLowerCase().trim()
      if (!q) return this.rows
      return this.rows.filter((r) => (r.employee_name || '').toLowerCase().includes(q))
    },
    dirtyCount() {
      return this.rows.filter((r) => r._dirty).length
    },
  },
  methods: {
    initials(name) { return this.initialsFn(name) },
    resumo(row) { return this.resumoFn(row) },
    toggle(id) {
      this.expandedId = this.expandedId === id ? null : id
      this.tabIdx = 0
    },
    markDirty(row) {
      if (!this.editing) return
      row._dirty = true
      this.$emit('dirty')
    },
  },
}
</script>

<style scoped>
.cards-view {
  display: flex;
  flex-direction: column;
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  min-height: 680px;
}
.cards-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid #e0e0e0;
}
.cards-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 4px;
}
.is-readonly .field__input,
.is-readonly .field__select {
  color: #607d8b;
  cursor: default;
}
.is-readonly .field__input-wrap,
.is-readonly .field__select {
  background: #fafafa;
}
.cards-search__input {
  flex: 1;
  border: none;
  border-bottom: 1px solid #bdbdbd;
  outline: none;
  font-size: 13px;
  padding: 4px 2px;
  background: transparent;
}
.cards-list {
  flex: 1;
  overflow: auto;
  padding: 12px;
}
.seller-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin: 0 0 10px;
  overflow: hidden;
  background: #fff;
}
.seller-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  cursor: pointer;
  min-height: 44px;
}
.seller-card__head:hover { background: #fafafa; }
.seller-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #546e7a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  flex: none;
}
.seller-card__meta { flex: 1; min-width: 0; }
.seller-card__name {
  font-size: 13px;
  font-weight: 500;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.seller-card__resumo { font-size: 11px; color: #78909c; margin-top: 1px; }
.seller-card__body { border-top: 1px solid #eeeeee; }
.seller-card__tabs { display: flex; }
.seller-card__tab {
  flex: 1;
  padding: 10px 4px;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  background: #fff;
  border: none;
  border-bottom: 2px solid transparent;
  color: #78909c;
}
.seller-card__tab--active {
  border-bottom-color: #1976d2;
  color: #1976d2;
}
.seller-card__fields {
  padding: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  background: #fafbfc;
}
.field__label { font-size: 10.5px; color: #757575; margin-bottom: 2px; }
.field__input-wrap {
  display: flex;
  align-items: baseline;
  gap: 5px;
  border-bottom: 1px solid #b0bec5;
  padding: 3px 0 4px;
}
.field__pre, .field__suf { font-size: 12px; color: #78909c; }
.field__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  padding: 0;
}
.field__input:focus { background: #e3f2fd; }
.field__select {
  width: 100%;
  border: none;
  border-bottom: 1px solid #b0bec5;
  outline: none;
  font-size: 13px;
  background: transparent;
  padding: 3px 0 4px;
  cursor: pointer;
}
.field__input::-webkit-outer-spin-button,
.field__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.field__input[type='number'] { -moz-appearance: textfield; }
.cards-footer {
  position: sticky;
  bottom: 0;
  padding: 10px 12px;
  background: #fff;
  border-top: 1px solid #e0e0e0;
}
</style>
